import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Bed, Bath, Maximize, ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatPrice, formatNumber } from '@/utils/format';
import { StatusBadge } from './StatusBadge';
import { isNewListing } from '@/utils/statusModel';
import { FavoriteButton } from './FavoriteButton';
import { ListingImage } from './ListingImage';
import { MlsBadge } from './MlsBadge';

const TAG_STYLES = {
  New: 'bg-blue-600 text-white',
  'Price Drop': 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  'Open House': 'bg-white/90 text-slate-900 border border-slate-200',
};

// Primary listing card used across the app. Includes a lightweight photo carousel
// with dots, status + contextual tags, favorite toggle, meta row and agent chip.
export const ListingCard = ({ listing, view = 'grid' }) => {
  const [index, setIndex] = useState(0);
  const touchRef = useRef({ x: 0, y: 0 });
  const swipedRef = useRef(false);
  if (!listing) return null;
  const photos = listing.photos?.length ? listing.photos : ['/placeholder.jpg'];
  const total = photos.length;

  const go = (e, dir) => {
    e.preventDefault();
    e.stopPropagation();
    setIndex((i) => (i + dir + total) % total);
  };

  const handleTouchStart = (e) => {
    touchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    swipedRef.current = false;
  };

  const handleTouchEnd = (e) => {
    if (total <= 1) return;
    const dx = e.changedTouches[0].clientX - touchRef.current.x;
    const dy = e.changedTouches[0].clientY - touchRef.current.y;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
      swipedRef.current = true;
      setIndex((i) => (i + (dx < 0 ? 1 : -1) + total) % total);
    }
  };

  const handleClickCapture = (e) => {
    if (swipedRef.current) {
      e.preventDefault();
      e.stopPropagation();
      swipedRef.current = false;
    }
  };

  const isList = view === 'list';

  return (
    <Link
      to={`/listing/${listing.id}`}
      data-testid="listing-card"
      className={cn(
        'group relative block overflow-hidden rounded-2xl bg-white border border-slate-200 shadow-md hover:shadow-xl transition-shadow duration-200',
        isList && 'sm:flex'
      )}
    >
      {/* Image / carousel */}
      <div
        data-testid="listing-card-carousel"
        className={cn('relative overflow-hidden bg-slate-100', isList ? 'sm:w-72 sm:shrink-0 aspect-[4/3] sm:aspect-auto' : 'aspect-[4/3]')}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onClickCapture={handleClickCapture}
      >
        <ListingImage
          src={photos[index]}
          alt={`${listing.type} at ${listing.address}, ${listing.city}`}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
        />
        {/* Top badges */}
        <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
          <StatusBadge listing={listing} />
          {isNewListing(listing) && (
            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-emerald-600 text-white">
              New
            </span>
          )}
          {(listing.tags || []).map((tag) => (
            <span key={tag} className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium', TAG_STYLES[tag])}>
              {tag}
            </span>
          ))}
        </div>
        <div className="absolute right-3 top-3">
          <FavoriteButton id={listing.id} />
        </div>

        {/* Carousel controls */}
        {total > 1 && (
          <>
            <button type="button" aria-label="Previous photo" onClick={(e) => go(e, -1)} className="absolute left-2 top-1/2 -translate-y-1/2 grid h-8 w-8 place-items-center rounded-full bg-white/85 text-slate-700 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm">
              <ChevronLeft size={18} />
            </button>
            <button type="button" aria-label="Next photo" onClick={(e) => go(e, 1)} className="absolute right-2 top-1/2 -translate-y-1/2 grid h-8 w-8 place-items-center rounded-full bg-white/85 text-slate-700 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm">
              <ChevronRight size={18} />
            </button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {photos.map((_, i) => (
                <span key={i} className={cn('h-1.5 rounded-full transition-all', i === index ? 'w-4 bg-white' : 'w-1.5 bg-white/60')} />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Body */}
      <div className="p-4 sm:p-5 flex-1">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p data-testid="listing-card-price" className="text-xl font-semibold tabular-nums text-slate-900">
            {formatPrice(listing.price, listing.isRental)}
          </p>
          <span className="text-xs text-slate-500">${formatNumber(listing.pricePerSqft)}/sqft</span>
        </div>

        <div className="mt-2 flex items-center gap-4 text-sm text-slate-600">
          <span className="inline-flex items-center gap-1"><Bed size={16} className="text-slate-500" />{listing.beds} bd</span>
          <span className="inline-flex items-center gap-1"><Bath size={16} className="text-slate-500" />{listing.baths} ba</span>
          <span className="inline-flex items-center gap-1"><Maximize size={16} className="text-slate-500" />{formatNumber(listing.sqft)} sqft</span>
        </div>

        <p className="mt-2 flex items-start gap-1 text-sm text-slate-600">
          <MapPin size={16} className="mt-0.5 shrink-0 text-slate-400" />
          <span className="truncate">{`${[listing.streetAddress, listing.city, listing.state].filter(Boolean).join(', ')}${listing.zip ? ` ${listing.zip}` : ''}`.trim()}</span>
        </p>

        <div className="mt-3 flex items-center justify-end gap-2 border-t border-slate-100 pt-3">
          <MlsBadge listing={listing} />
          <span className="text-xs text-slate-400">{listing.apiStatus === 'U' ? 'Off-market' : `${listing.daysOnMarket}d on market`}</span>
        </div>
      </div>
    </Link>
  );
};
